import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter
} from '@/components/ui/card';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Mail, ArrowLeft, Save, Eye, Code, FileText, RefreshCw, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { cn } from '@/lib/utils';

// Interface para o template de email
interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  is_active: boolean;
}

// Interface para os dados de preview
interface PreviewData {
  name: string;
  email: string;
  company: string;
  date: string;
  link: string;
  code: string;
  product: string;
  amount: string;
  invoice_number: string;
  [key: string]: string;
}

// Schema de validação para o formulário
const templateSchema = z.object({
  name: z.string().min(3, { message: 'O nome deve ter pelo menos 3 caracteres' }),
  subject: z.string().min(3, { message: 'O assunto deve ter pelo menos 3 caracteres' }),
  body: z.string().min(10, { message: 'O corpo do email deve ter pelo menos 10 caracteres' }),
  is_active: z.boolean().default(true)
});

type TemplateFormValues = z.infer<typeof templateSchema>;

const EmailTemplateEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editorMode, setEditorMode] = useState<'simple' | 'html'>('simple');
  const [previewHtml, setPreviewHtml] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [previewData] = useState<PreviewData>({
    name: 'João Silva',
    email: 'joao.silva@exemplo.com',
    company: 'Empresa ABC Ltda',
    date: new Date().toLocaleDateString('pt-BR'),
    link: 'https://exemplo.com/confirmar',
    code: '123456',
    product: 'Plano Premium',
    amount: 'R$ 99,90',
    invoice_number: 'INV-2023-001'
  });

  // Formulário para editar template
  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: '',
      subject: '',
      body: '',
      is_active: true
    }
  });

  // Carregar dados do template
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('email_templates')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        
        if (data) {
          setTemplate(data);
          form.reset({
            name: data.name,
            subject: data.subject,
            body: data.body,
            is_active: data.is_active
          });
        }
      } catch (error: any) {
        console.error('Erro ao carregar template:', error);
        toast.error('Erro ao carregar template de email', {
          description: error.message
        });
        navigate('/email-templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id, navigate, form]);

  // Gerar preview do template
  const generatePreview = () => {
    try {
      console.log('Gerando preview. Modo atual:', editorMode);
      const bodyContent = form.getValues('body');
      console.log('Conteúdo do corpo:', bodyContent);

      let previewContent = bodyContent;
      
      // Substituir variáveis pelos valores de exemplo
      Object.keys(previewData).forEach(key => {
        const regex = new RegExp(`{${key}}`, 'g');
        previewContent = previewContent.replace(regex, previewData[key]);
      });

      // Adicionar estilos básicos para o preview
      const styledHtml = `
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
          h1, h2, h3 { color: #2563eb; }
          a { color: #2563eb; text-decoration: none; }
          a:hover { text-decoration: underline; }
          .footer { margin-top: 30px; font-size: 12px; color: #666; border-top: 1px solid #eee; padding-top: 10px; }
        </style>
        ${previewContent}
      `;
      
      console.log('HTML gerado para preview:', styledHtml);
      setPreviewHtml(styledHtml);
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
      toast.error('Erro ao gerar preview do template');
    }
  };

  // Atualizar preview quando o conteúdo do corpo mudar
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'body') {
        generatePreview();
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Gerar preview inicial após carregar o template
  useEffect(() => {
    if (!loading && template) {
      generatePreview();
    }
  }, [loading, template]);

  // Monitorar mudanças no editorMode
  useEffect(() => {
    console.log('Editor mode alterado para:', editorMode);
  }, [editorMode]);

  // Salvar template
  const handleSaveTemplate = async (values: TemplateFormValues) => {
    if (!id) return;
    
    setSubmitting(true);
    try {
      // Atualizar o template principal
      const { error } = await supabase
        .from('email_templates')
        .update(values)
        .eq('id', id);

      if (error) throw error;
      
      // Buscar a última versão
      const { data: versionData, error: versionFetchError } = await supabase
        .from('email_template_versions')
        .select('*')
        .eq('email_template_id', id)
        .order('version_number', { ascending: false })
        .limit(1);
        
      if (versionFetchError) throw versionFetchError;
      
      // Determinar o próximo número de versão
      const nextVersionNumber = versionData && versionData.length > 0 
        ? versionData[0].version_number + 1 
        : 1;
      
      // Criar nova versão do template
      const newVersionData = {
        email_template_id: id,
        version_number: nextVersionNumber,
        subject: values.subject,
        html_content: values.body,
        plain_text_content: null, // Opcional
        active: true
      };
      
      // Desativar versões anteriores
      if (versionData && versionData.length > 0) {
        const { error: deactivateError } = await supabase
          .from('email_template_versions')
          .update({ active: false })
          .eq('email_template_id', id);
          
        if (deactivateError) throw deactivateError;
      }
      
      // Inserir nova versão
      const { error: insertError } = await supabase
        .from('email_template_versions')
        .insert([newVersionData]);
        
      if (insertError) throw insertError;
      
      toast.success('Template de email atualizado com sucesso');
      navigate('/email-templates');
    } catch (error: any) {
      console.error('Erro ao atualizar template:', error);
      toast.error('Erro ao atualizar template de email', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full px-6 py-6">
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Carregando template...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => navigate('/email-templates')}
            className="mr-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-extrabold">Editar Template de Email</h1>
            <p className="text-muted-foreground">
              Modifique as informações do template
            </p>
          </div>
        </div>
        <Button 
          onClick={form.handleSubmit(handleSaveTemplate)} 
          disabled={submitting}
          className="gap-1"
        >
          <Save className="h-4 w-4" /> 
          {submitting ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulário */}
        <Card>
          <CardHeader>
            <CardTitle>Informações do Template</CardTitle>
            <CardDescription>
              Atualize as informações do template de email
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSaveTemplate)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome do Template</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Boas-vindas, Confirmação de Pagamento, etc." {...field} />
                      </FormControl>
                      <FormDescription>
                        Um nome descritivo para identificar o template.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assunto do Email</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Bem-vindo à nossa plataforma!" {...field} />
                      </FormControl>
                      <FormDescription>
                        O assunto que aparecerá no email. Você pode usar variáveis como {'{'}nome{'}'}.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Separator className="my-4" />
                
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-extrabold">Corpo do Email</h3>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant={editorMode === 'simple' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        console.log('Alterando para modo simple');
                        setEditorMode('simple');
                      }}
                      className="gap-1"
                      type="button"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Texto Simples
                    </Button>
                    <Button 
                      variant={editorMode === 'html' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        console.log('Alterando para modo html');
                        setEditorMode('html');
                      }}
                      className="gap-1"
                      type="button"
                    >
                      <Code className="h-3.5 w-3.5" />
                      HTML
                    </Button>
                  </div>
                </div>
                
                <FormField
                  control={form.control}
                  name="body"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea 
                          placeholder={editorMode === 'simple' 
                            ? "Conteúdo do email. Você pode usar variáveis como {nome} que serão substituídas pelos valores reais." 
                            : "<h1>Olá {name},</h1>\n<p>Bem-vindo à nossa plataforma!</p>\n<p>Clique <a href='{link}'>aqui</a> para confirmar seu cadastro.</p>"
                          }
                          className={cn(
                            "min-h-[300px]",
                            editorMode === 'html' ? "font-mono text-xs" : ""
                          )}
                          {...field} 
                          onFocus={() => {
                            console.log('Editor focado. Modo atual:', editorMode);
                            console.log('Classes aplicadas:', 
                              cn("min-h-[300px]", editorMode === 'html' ? "font-mono text-xs" : "")
                            );
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        {editorMode === 'simple' 
                          ? "O conteúdo do email. Suporta HTML e variáveis entre chaves simples como {nome}."
                          : "Insira o código HTML do email. Você pode usar variáveis entre chaves simples como {nome}."
                        }
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Ativo</FormLabel>
                        <FormDescription>
                          Determina se este template pode ser usado para enviar emails
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Preview do Template</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  console.log('Atualizando preview manualmente');
                  generatePreview();
                }}
                className="gap-1"
                type="button"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Atualizar Preview
              </Button>
            </div>
            <CardDescription>
              Visualização do email com dados de exemplo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="preview" className="mt-4">
              <TabsList className="mb-2">
                <TabsTrigger value="preview">Preview</TabsTrigger>
                <TabsTrigger value="variables">Variáveis Disponíveis</TabsTrigger>
              </TabsList>
              
              {/* Preview */}
              <TabsContent value="preview" className="border rounded-md p-4 mt-2">
                <div className="flex justify-between items-center mb-4">
                  <CardTitle>Preview do Template</CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      console.log('Atualizando preview manualmente');
                      generatePreview();
                    }}
                    className="gap-1"
                    type="button"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Atualizar Preview
                  </Button>
                </div>
                
                <div className="border rounded-md overflow-hidden">
                  <iframe 
                    srcDoc={previewHtml}
                    title="Email Preview"
                    className="w-full h-[400px] border-0"
                    sandbox="allow-same-origin"
                  />
                </div>
              </TabsContent>
              
              {/* Variáveis Disponíveis */}
              <TabsContent value="variables" className="border rounded-md p-4 mt-2">
                <div className="mb-4">
                  <h3 className="text-lg font-extrabold mb-2">Variáveis Disponíveis</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Você pode usar as seguintes variáveis no seu template. Elas serão substituídas pelos valores reais quando o email for enviado.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div><code>{'{name}'}</code> - João Silva</div>
                    <div><code>{'{email}'}</code> - joao.silva@exemplo.com</div>
                    <div><code>{'{company}'}</code> - Empresa ABC Ltda</div>
                    <div><code>{'{date}'}</code> - {new Date().toLocaleDateString('pt-BR')}</div>
                    <div><code>{'{link}'}</code> - https://exemplo.com/confirmar</div>
                    <div><code>{'{code}'}</code> - 123456</div>
                    <div><code>{'{product}'}</code> - Plano Premium</div>
                    <div><code>{'{amount}'}</code> - R$ 99,90</div>
                    <div><code>{'{invoice_number}'}</code> - INV-2023-001</div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailTemplateEdit; 